import { useRef, useState, useCallback, useEffect } from "react"
import Script from "next/script"
import Bugsnag from "@bugsnag/js"

import CreditCardBrands from "@src/components/CreditCardBrands"
import AuthorizeNetCardForm, {
  type AuthorizeNetCardFormRef,
} from "@src/components/AuthorizeNetCardForm"
import useAuthorizeNet from "@src/hooks/use-authorize-net"

import type { AcceptJsOpaqueData } from "@src/entities/types/authorize-net"

/** Get Accept.js script URL based on environment */
function getAcceptJsScriptUrl(): string {
  const env = process.env.NEXT_PUBLIC_AUTHORIZE_NET_ENV || "sandbox"
  return env === "production"
    ? "https://js.authorize.net/v1/Accept.js"
    : "https://jstest.authorize.net/v1/Accept.js"
}

export interface AuthorizeNetPaymentFormProps {
  /**
   * Called with tokenized payment data when card is successfully tokenized.
   * The parent component should call the GraphQL mutation with this data.
   */
  getPaymentToken: (opaqueData: AcceptJsOpaqueData) => void | Promise<void>
  /** Whether the form should be enabled for submission */
  enabled: boolean
}

/**
 * Authorize.net payment form for checkout.
 *
 * This component:
 * - Loads Accept.js from Authorize.net CDN
 * - Renders a card input form
 * - Tokenizes card data on submit
 * - Returns opaque data to parent for GraphQL mutation
 *
 * @example
 * ```tsx
 * <AuthorizeNetPaymentForm
 *   enabled={billingEnabled && !isProcessing}
 *   getPaymentToken={async ({ dataDescriptor, dataValue }) => {
 *     // Call PAY_WITH_CARD mutation
 *     await payWithCard({ dataDescriptor, dataValue })
 *   }}
 * />
 * ```
 */
export default function AuthorizeNetPaymentForm({
  getPaymentToken,
  enabled,
}: AuthorizeNetPaymentFormProps) {
  const cardFormRef = useRef<AuthorizeNetCardFormRef>(null)
  const isSubmittingRef = useRef(false)
  const { tokenize, isLoading, error, clearError } = useAuthorizeNet()
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [scriptError, setScriptError] = useState<string | null>(null)

  // Check if Accept.js is already available on mount (e.g., from browser cache)
  // This handles cases where the script loads before React mounts or on re-renders
  useEffect(() => {
    if (typeof window !== "undefined" && window.Accept && !scriptLoaded) {
      setScriptLoaded(true)
    }
  }, [scriptLoaded])

  const handleSubmit = useCallback(async () => {
    if (isSubmittingRef.current) return
    if (!enabled || isLoading) return

    isSubmittingRef.current = true

    // Clear any previous errors
    clearError()

    // Validate form
    if (!cardFormRef.current?.validate()) {
      isSubmittingRef.current = false
      return
    }

    try {
      // Get card data from form
      const cardData = cardFormRef.current.getCardData()

      // Tokenize with Accept.js
      const opaqueData = await tokenize(cardData)

      // Pass token to parent component
      await getPaymentToken(opaqueData)
    } catch (err) {
      // Error is already handled by useAuthorizeNet hook
      // and will be displayed via the error state
      console.error("Payment tokenization failed:", err)
    } finally {
      isSubmittingRef.current = false
    }
  }, [enabled, isLoading, tokenize, getPaymentToken, clearError])

  const handleScriptLoad = useCallback(() => {
    // Verify Accept is actually available on window
    if (typeof window !== "undefined" && window.Accept) {
      setScriptLoaded(true)
      setScriptError(null)
    } else {
      // Script loaded but Accept not available yet - poll for it
      let attempts = 0
      const maxAttempts = 50 // 5 seconds max
      const pollInterval = setInterval(() => {
        attempts++
        if (typeof window !== "undefined" && window.Accept) {
          setScriptLoaded(true)
          setScriptError(null)
          clearInterval(pollInterval)
        } else if (attempts >= maxAttempts) {
          setScriptError("Payment system failed to initialize. Please refresh the page.")
          clearInterval(pollInterval)
        }
      }, 100)
    }
  }, [])

  const handleScriptError = useCallback(() => {
    const errorMessage = "Failed to load payment system. Please refresh and try again."
    setScriptError(errorMessage)
    Bugsnag.notify(new Error("Accept.js script failed to load"))
  }, [])

  // Combine error states
  const displayError = error || scriptError

  return (
    <>
      {/* Card Form */}
      <AuthorizeNetCardForm
        ref={cardFormRef}
        disabled={!enabled || isLoading || !scriptLoaded}
        errorMessage={displayError}
      />

      {/* Submit Button and Card Brands */}
      <div className="flex items-center justify-between">
        <CreditCardBrands size="small" />
        <button
          type="button"
          className="bg-black inline-block px-12 py-2 font-medium rounded-sm text-sm text-white uppercase hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSubmit}
          disabled={!enabled || isLoading || !scriptLoaded}
        >
          {isLoading ? "Processing..." : "Pay With Card"}
        </button>
      </div>

      {/* Accept.js Script */}
      <Script
        id="authorize-net-accept-js"
        src={getAcceptJsScriptUrl()}
        onLoad={handleScriptLoad}
        onError={handleScriptError}
        strategy="afterInteractive"
      />
    </>
  )
}

